import { AcademicMajorValue } from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import {
  SchoolApiAutocompleteOption,
  AutocompleteOption,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import {
  UserAcademicHistory,
  UserAcademicHistoryRequest,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userAcademicHistory.api';

import { ResumeQuestionField } from './questions.config';
import { FormValue, FormValues } from './types';
import {
  convertDateToString,
  convertStringValue,
  convertBooleanValue,
  convertDateFromString,
} from './utils';

// Parameter object types for refactored functions
export interface BuildAcademicHistoryParams {
  formValues: FormValues;
  formFields: ResumeQuestionField[];
  institutionId?: number | null;
  majorIds?: number[];
  isUpdate?: boolean;
}

export interface PrePopulateFormParams {
  academicHistory: UserAcademicHistory[];
  questionNumber: number;
  formFields: ResumeQuestionField[];
}

const isSchoolAutocompleteOption = (value: FormValue): value is SchoolApiAutocompleteOption => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'school' in value &&
    typeof (value as SchoolApiAutocompleteOption).school === 'string'
  );
};

const isBaseAutocompleteOption = (value: FormValue): value is AutocompleteOption => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    'value' in value &&
    typeof (value as AutocompleteOption).label === 'string' &&
    typeof (value as AutocompleteOption).value === 'string'
  );
};

const isMajorOption = (value: FormValue): value is AcademicMajorValue => {
  return typeof value === 'object' && value !== null && 'academic_majors_id' in value;
};

const convertInstitutionName = (values: FormValue[]): string | undefined => {
  if (values.length === 0) return undefined;

  const value = values[0] as AutocompleteOption;

  if (isSchoolAutocompleteOption(value)) {
    return value.school;
  }

  if (isBaseAutocompleteOption(value)) {
    return value.value || value.label;
  }

  return undefined;
};

const convertMajorIds = (values: FormValue[]): number[] => {
  return values.filter(isMajorOption).map((v) => v.academic_majors_id);
};

const convertStringField = (values: FormValue[]): string | undefined => {
  if (values.length === 0) return undefined;

  return convertStringValue(values[0]);
};

const convertBooleanField = (values: FormValue[]): boolean | undefined => {
  if (values.length === 0) return undefined;

  return convertBooleanValue(values[0]);
};

const convertDateField = (values: FormValue[]): string | undefined => {
  if (values.length === 0) return undefined;

  return convertDateToString(values[0]);
};

// Lookup table for converters
const converters = {
  institution_name: convertInstitutionName,
  awards_accomplishments: convertStringField,
  institution_city: convertStringField,
  institution_state: convertStringField,
  did_graduate: convertBooleanField,
  majorIds: convertMajorIds,
  majors: convertMajorIds,
  graduation_date: convertDateField,
  expected_graduation_date: convertDateField,
} as const;

export const convertValueForAcademicHistory = (
  column: string,
  values: FormValue[],
): string | number | boolean | number[] | undefined => {
  if (values.length === 0) return undefined;

  // Use specific converter if available
  if (column in converters) {
    const converter = converters[column as keyof typeof converters];

    return converter(values);
  }

  // Fallback to string conversion
  return convertStringValue(values[0]);
};

export const buildAcademicHistoryFromFormValues = ({
  formValues,
  formFields,
  institutionId,
  majorIds,
  isUpdate = false,
}: BuildAcademicHistoryParams): UserAcademicHistoryRequest => {
  const academicHistoryData = formFields.reduce((acc, field) => {
    if (!field.academicHistoryKey || !formValues[field.key]) return acc;

    const value = convertValueForAcademicHistory(field.academicHistoryKey, formValues[field.key]);

    if (value !== undefined) {
      // special case for majors -- convert from "majors" to "majorIds"
      const academicHistoryKey =
        field.academicHistoryKey === 'majors' ? 'majorIds' : field.academicHistoryKey;

      return {
        ...acc,
        [academicHistoryKey]: value,
      };
    }

    return acc;
  }, {} as UserAcademicHistoryRequest);

  return {
    ...academicHistoryData,
    // For updates: always include institution_id (even if null to clear it)
    // For creates: only include if it has a value
    ...(isUpdate
      ? { institution_id: institutionId ?? null }
      : institutionId && { institution_id: institutionId }),
    ...(majorIds && majorIds.length > 0 && { majorIds }),
  };
};

export const prePopulateFormWithAcademicHistory = ({
  academicHistory,
  questionNumber,
  formFields,
}: PrePopulateFormParams): {
  academicHistoryId: number;
  formValues: FormValues;
  selectedSchoolName: SchoolApiAutocompleteOption | null;
  selectedMajorsIds: number[];
} | null => {
  const targetIndex = questionNumber - 1; // since the array is 0-indexed
  const targetEntry = academicHistory[targetIndex];

  if (!targetEntry) return null;

  const initialAcc = {
    academicHistoryId: targetEntry.id,
    formValues: {} as FormValues,
    selectedSchoolName: null as SchoolApiAutocompleteOption | null,
    selectedMajorsIds: [] as number[],
  };

  return formFields.reduce((acc, field) => {
    if (!field.academicHistoryKey) return acc;

    const academicHistoryValue = targetEntry[field.academicHistoryKey];

    if (academicHistoryValue === undefined || academicHistoryValue === null) return acc;

    switch (field.academicHistoryKey) {
      case 'institution_name': {
        // Expect normalized data with name and institution_id from API
        if (
          typeof targetEntry === 'object' &&
          targetEntry !== null &&
          'institution_name' in targetEntry &&
          'institution_id' in targetEntry
        ) {
          const institutionData = targetEntry as {
            institution_name: string;
            institution_id: number;
          };

          const schoolOption: SchoolApiAutocompleteOption = {
            label: institutionData.institution_name,
            value: institutionData.institution_name,
            school: institutionData.institution_name,
            institution_id: institutionData.institution_id,
          };

          return {
            ...acc,
            formValues: {
              ...acc.formValues,
              [field.key]: [schoolOption],
            },
            selectedSchoolName: schoolOption,
          };
        }

        // Fallback to null if data isn't in expected format
        return acc;
      }

      case 'institution_city':
      case 'institution_state':
      case 'awards_accomplishments': {
        const stringValue = String(academicHistoryValue);

        return {
          ...acc,
          formValues: {
            ...acc.formValues,
            [field.key]: [stringValue],
          },
        };
      }

      case 'did_graduate': {
        const booleanValue = Boolean(academicHistoryValue);

        return {
          ...acc,
          formValues: {
            ...acc.formValues,
            [field.key]: [booleanValue ? 'Yes' : 'No'],
          },
        };
      }

      case 'graduation_date': {
        const dateString = String(academicHistoryValue);

        return {
          ...acc,
          formValues: {
            ...acc.formValues,
            [field.key]: [convertDateFromString(dateString)],
          },
        };
      }

      case 'gpa':
      case 'major_gpa': {
        const numericValue = Number(academicHistoryValue);

        return {
          ...acc,
          formValues: {
            ...acc.formValues,
            [field.key]: [numericValue],
          },
        };
      }

      case 'degree_id': {
        const degreeId = Number(academicHistoryValue);

        return {
          ...acc,
          formValues: {
            ...acc.formValues,
            [field.key]: [degreeId],
          },
        };
      }

      case 'majors': {
        if (Array.isArray(targetEntry.majors) && targetEntry.majors.length > 0) {
          const majorOptions: AcademicMajorValue[] = targetEntry.majors.map((major) => ({
            id: major.id,
            name: major.name,
            academic_majors_id: major.id,
            major_gpa: 0,
          }));

          return {
            ...acc,
            formValues: {
              ...acc.formValues,
              [field.key]: majorOptions,
            },
            selectedMajorsIds: targetEntry.majors.map((major) => major.id),
          };
        }

        return acc;
      }

      default:
        return acc;
    }
  }, initialAcc);
};
